import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ReservationStatus, ReservationView } from "@glowin/schemas";
import { api, ApiError } from "../api.js";
import { ErrorBox } from "./MenuPage.js";
import { Modal } from "../modal.js";
import { usePageTitle, pluralize } from "../ui.js";
import {
  actionsFor,
  clockLabel,
  dayHeading,
  phoneLabel,
  readBookingForm,
  restaurantToday,
  shiftDate,
  statusLabel,
  statusTone,
  type BookingForm
} from "../reservations-model.js";
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  MessageIcon,
  PhoneIcon,
  PlusIcon,
  UserIcon,
  UsersIcon,
  WithIcon
} from "../icons.js";

/** One day of table bookings: who is coming, when, and how many. */
export function ReservationsPage() {
  usePageTitle("Reservations");
  const { restaurantId = "" } = useParams();
  const qc = useQueryClient();

  const profile = useQuery({
    queryKey: ["profile", restaurantId],
    queryFn: () => api.getProfile(restaurantId),
    retry: false
  });
  const settings = useQuery({ queryKey: ["settings", restaurantId], queryFn: () => api.getSettings(restaurantId) });
  const today = restaurantToday(profile.data?.timezone);
  const [picked, setPicked] = useState<string | undefined>();
  const date = picked ?? today;
  const [adding, setAdding] = useState(false);

  const key = ["reservations", restaurantId, date];
  const day = useQuery({
    queryKey: key,
    queryFn: () => api.listReservations(restaurantId, date),
    // Bookings arrive from the phone while the page is open.
    refetchInterval: 30_000
  });

  const enabled = settings.data?.reservations?.enabled === true;

  return (
    <>
      <div className="page-head">
        <h1>Reservations</h1>
        <div className="page-actions">
          <button className="primary" onClick={() => setAdding(true)}>
            <WithIcon icon={<PlusIcon size={16} />}>Add booking</WithIcon>
          </button>
        </div>
      </div>

      {settings.data && !enabled ? (
        <div className="notice warn">
          The phone agent is not taking bookings. <Link to={`/r/${restaurantId}/settings`}>Turn it on in Settings</Link>.
        </div>
      ) : null}

      {/* The same controls as the order queue's, but with no upper bound: bookings are in the future. */}
      <div className="day-nav booking-day-nav">
        <button type="button" className="secondary" aria-label="Previous day" onClick={() => setPicked(shiftDate(date, -1))}>
          <ChevronLeftIcon size={16} />
        </button>
        <input
          type="date"
          aria-label="Show bookings for this day"
          value={date}
          onChange={(e) => setPicked(e.target.value || undefined)}
        />
        <button type="button" className="secondary" aria-label="Next day" onClick={() => setPicked(shiftDate(date, 1))}>
          <ChevronRightIcon size={16} />
        </button>
        {date !== today ? (
          <button type="button" className="ghost" onClick={() => setPicked(undefined)}>
            <WithIcon icon={<CalendarIcon size={15} />}>Today</WithIcon>
          </button>
        ) : null}
      </div>

      <section className="card" aria-labelledby="day-heading">
        <div className="day-summary">
          <h2 id="day-heading">{dayHeading(date, today)}</h2>
          {day.data ? (
            <span className="muted">
              {pluralize(day.data.bookings, "booking")} · {pluralize(day.data.guests, "guest")}
            </span>
          ) : null}
        </div>
        {day.isLoading ? <p className="muted">Loading bookings…</p> : null}
        {day.error ? <ErrorBox error={day.error} /> : null}
        {day.data && day.data.reservations.length === 0 ? <p className="muted">No bookings.</p> : null}
        {day.data && day.data.reservations.length > 0 ? (
          <ul className="booking-list">
            {day.data.reservations.map((r) => (
              <BookingRow
                key={r.reservationId}
                booking={r}
                onChanged={() => void qc.invalidateQueries({ queryKey: key })}
                restaurantId={restaurantId}
              />
            ))}
          </ul>
        ) : null}
      </section>

      {adding ? (
        <AddBookingDialog
          restaurantId={restaurantId}
          date={date}
          onClose={() => setAdding(false)}
          onAdded={(added) => {
            setAdding(false);
            setPicked(added);
            void qc.invalidateQueries({ queryKey: ["reservations", restaurantId] });
          }}
        />
      ) : null}
    </>
  );
}

function BookingRow({
  booking: r,
  restaurantId,
  onChanged
}: {
  booking: ReservationView;
  restaurantId: string;
  onChanged: () => void;
}) {
  const [confirming, setConfirming] = useState<ReservationStatus | undefined>();
  const change = useMutation({
    mutationFn: (to: ReservationStatus) => api.setReservationStatus(restaurantId, r.reservationId, to),
    onSuccess: () => {
      setConfirming(undefined);
      onChanged();
    }
  });
  const cancelled = r.status === "CANCELLED";

  return (
    <li className={cancelled ? "booking cancelled" : "booking"}>
      <div className="booking-time">{clockLabel(r.time)}</div>
      <div className="booking-main">
        <div className="booking-who">
          <strong>{r.name}</strong>
          <span className="with-icon muted">
            <UsersIcon size={15} />
            <span>{pluralize(r.partySize, "guest")}</span>
          </span>
          <span className={`badge tone-${statusTone(r.status)}`}>{statusLabel(r.status)}</span>
        </div>
        <div className="booking-meta muted">
          {r.phoneE164 ? (
            <a className="with-icon" href={`tel:${r.phoneE164}`}>
              <PhoneIcon size={14} />
              <span>{phoneLabel(r.phoneE164)}</span>
            </a>
          ) : null}
          <span className="with-icon">
            {r.source === "PHONE" ? <PhoneIcon size={14} /> : <UserIcon size={14} />}
            <span>{r.source === "PHONE" ? "Booked by phone" : "Added by staff"}</span>
          </span>
        </div>
        {r.notes ? (
          <p className="booking-notes with-icon">
            <MessageIcon size={14} />
            <span>{r.notes}</span>
          </p>
        ) : null}
        {change.error ? <ErrorBox error={change.error} /> : null}
      </div>
      <div className="booking-actions">
        {confirming ? (
          <>
            <span className="muted">Cancel this booking?</span>
            <button className="danger" disabled={change.isPending} onClick={() => change.mutate(confirming)}>
              Yes, cancel
            </button>
            <button className="ghost" onClick={() => setConfirming(undefined)}>
              Keep
            </button>
          </>
        ) : (
          actionsFor(r.status).map((a) => (
            <button
              key={a.to}
              className="ghost"
              disabled={change.isPending}
              aria-label={`${a.label}: ${r.name} at ${clockLabel(r.time)}`}
              onClick={() => (a.confirm ? setConfirming(a.to) : change.mutate(a.to))}
            >
              {a.label}
            </button>
          ))
        )}
      </div>
    </li>
  );
}

function AddBookingDialog({
  restaurantId,
  date,
  onClose,
  onAdded
}: {
  restaurantId: string;
  date: string;
  onClose: () => void;
  onAdded: (date: string) => void;
}) {
  const [form, setForm] = useState<BookingForm>({ date, time: "19:00", partySize: "2", name: "", phone: "", notes: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof BookingForm, string>>>({});
  const save = useMutation({
    mutationFn: (req: Parameters<typeof api.createReservation>[1]) => api.createReservation(restaurantId, req),
    onSuccess: (created) => onAdded(created.date)
  });
  const set = (k: keyof BookingForm) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  const field = (k: keyof BookingForm, label: string, input: JSX.Element) => (
    <label className="field">
      <span>{label}</span>
      {input}
      {errors[k] ? <span className="field-error">{errors[k]}</span> : null}
    </label>
  );
  const invalid = (k: keyof BookingForm) => (errors[k] ? true : undefined);

  return (
    <Modal label="Add booking" onClose={onClose}>
      <form
        className="modal-form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const read = readBookingForm(form);
          if (!read.ok) return setErrors(read.errors);
          setErrors({});
          save.mutate(read.request);
        }}
      >
        <h2>Add booking</h2>
        <div className="field-row three">
          {field("date", "Date", <input type="date" value={form.date} onChange={set("date")} aria-invalid={invalid("date")} />)}
          {field("time", "Time", <input type="time" step={900} value={form.time} onChange={set("time")} aria-invalid={invalid("time")} />)}
          {field(
            "partySize",
            "Guests",
            <input type="number" min={1} max={500} value={form.partySize} onChange={set("partySize")} aria-invalid={invalid("partySize")} />
          )}
        </div>
        {field("name", "Name", <input value={form.name} maxLength={80} onChange={set("name")} aria-invalid={invalid("name")} />)}
        {field(
          "phone",
          "Phone (optional)",
          <input type="tel" value={form.phone} onChange={set("phone")} aria-invalid={invalid("phone")} />
        )}
        {field("notes", "Notes (optional)", <input value={form.notes} maxLength={300} onChange={set("notes")} />)}
        {save.error ? (
          <ErrorBox error={save.error instanceof ApiError ? save.error : new Error("Could not add the booking.")} />
        ) : null}
        <div className="modal-actions">
          <button type="button" className="ghost" onClick={onClose}>
            Close
          </button>
          <button type="submit" className="primary" disabled={save.isPending}>
            {save.isPending ? "Adding…" : "Add booking"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
